import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import { GripVertical, X, Plus, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Operators by field type
const OPERATORS_BY_TYPE = {
  'Short Text': [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'Long Text': [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'Email': [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'Website': [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'Dropdown': [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'Multi Choice': [
    { value: 'contains', label: 'includes' },
    { value: 'not_contains', label: 'does not include' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'Numbers': [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'greater_than', label: 'is greater than' },
    { value: 'less_than', label: 'is less than' },
    { value: 'greater_or_equal', label: 'is greater than or equal' },
    { value: 'less_or_equal', label: 'is less than or equal' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'Date': [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'before', label: 'is before' },
    { value: 'after', label: 'is after' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'File Upload': [
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'User': [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  'Table': [
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
};

// Default operators for unknown field types
const DEFAULT_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

// Check if operator requires a value
const operatorNeedsValue = (operator) => {
  return !['is_empty', 'is_not_empty'].includes(operator);
};

// Get operators for a field type
const getOperatorsForType = (fieldType) => {
  return OPERATORS_BY_TYPE[fieldType] || DEFAULT_OPERATORS;
};

// Generate unique ID
const generateId = () => `cond-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Single Condition Row Component
function ConditionRow({
  condition,
  availableFields,
  onUpdate,
  onDelete,
  isOnly,
  fieldMap
}) {
  const selectedField = fieldMap[condition.fieldId];
  const operators = selectedField ? getOperatorsForType(selectedField.type) : DEFAULT_OPERATORS;
  const needsValue = operatorNeedsValue(condition.operator);

  // Get options for dropdown/multi-choice fields
  const fieldOptions = useMemo(() => {
    if (!selectedField) return [];
    if (selectedField.type === 'Dropdown' || selectedField.type === 'Multi Choice') {
      return selectedField.options || [];
    }
    return [];
  }, [selectedField]);

  const handleFieldChange = (fieldId) => {
    const newField = fieldMap[fieldId];
    const newOperators = newField ? getOperatorsForType(newField.type) : DEFAULT_OPERATORS;
    // Reset operator and value when field changes
    onUpdate(condition.id, {
      fieldId,
      operator: newOperators[0]?.value || 'equals',
      value: ''
    });
  };

  const handleOperatorChange = (operator) => {
    onUpdate(condition.id, {
      operator,
      value: operatorNeedsValue(operator) ? condition.value : ''
    });
  };

  return (
    <div className="flex items-center gap-2 p-2 border rounded-lg bg-muted/30 group">
      {/* Drag handle placeholder */}
      <div className="text-muted-foreground cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical className="h-4 w-4" />
      </div>

      {/* Field selector */}
      <Select value={condition.fieldId || ''} onValueChange={handleFieldChange}>
        <SelectTrigger className="w-[180px] h-9 text-sm">
          <SelectValue placeholder="Select field..." />
        </SelectTrigger>
        <SelectContent>
          {availableFields.map((taskGroup) => (
            <SelectGroup key={taskGroup.taskId}>
              <SelectLabel className="text-xs text-muted-foreground font-normal">
                {taskGroup.taskName || `Task ${taskGroup.taskIndex + 1}`}
              </SelectLabel>
              {taskGroup.fields.map((field, fieldIndex) => (
                <SelectItem key={field.id} value={field.id}>
                  <span className="text-muted-foreground mr-1">Q{fieldIndex + 1}.</span>
                  {field.name}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>

      {/* Operator selector */}
      <Select value={condition.operator || 'equals'} onValueChange={handleOperatorChange}>
        <SelectTrigger className="w-[140px] h-9 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op.value} value={op.value}>
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value input */}
      {needsValue && (
        fieldOptions.length > 0 ? (
          <Select
            value={condition.value || ''}
            onValueChange={(value) => onUpdate(condition.id, { value })}
          >
            <SelectTrigger className="w-[140px] h-9 text-sm">
              <SelectValue placeholder="Select value..." />
            </SelectTrigger>
            <SelectContent>
              {fieldOptions.map((option) => (
                <SelectItem key={option.id || option.value} value={option.value}>
                  {option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={condition.value || ''}
            onChange={(e) => onUpdate(condition.id, { value: e.target.value })}
            placeholder="Enter value..."
            className="w-[140px] h-9 text-sm"
          />
        )
      )}

      {/* Delete button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => onDelete(condition.id)}
        disabled={isOnly}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Condition Group Component (for nested AND/OR logic)
function ConditionGroup({
  group,
  availableFields,
  fieldMap,
  onUpdate,
  onDelete,
  depth = 0,
  isOnly = false
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const addCondition = () => {
    const newCondition = {
      id: generateId(),
      fieldId: '',
      operator: 'equals',
      value: ''
    };
    onUpdate(group.id, {
      conditions: [...(group.conditions || []), newCondition]
    });
  };

  const addInnerGroup = () => {
    const newGroup = {
      id: generateId(),
      matchType: 'all',
      conditions: [{
        id: generateId(),
        fieldId: '',
        operator: 'equals',
        value: ''
      }],
      groups: []
    };
    onUpdate(group.id, {
      groups: [...(group.groups || []), newGroup]
    });
  };

  const updateCondition = (conditionId, updates) => {
    onUpdate(group.id, {
      conditions: (group.conditions || []).map(c =>
        c.id === conditionId ? { ...c, ...updates } : c
      )
    });
  };

  const deleteCondition = (conditionId) => {
    onUpdate(group.id, {
      conditions: (group.conditions || []).filter(c => c.id !== conditionId)
    });
  };

  const updateInnerGroup = (innerGroupId, updates) => {
    onUpdate(group.id, {
      groups: (group.groups || []).map(g =>
        g.id === innerGroupId ? { ...g, ...updates } : g
      )
    });
  };

  const deleteInnerGroup = (innerGroupId) => {
    onUpdate(group.id, {
      groups: (group.groups || []).filter(g => g.id !== innerGroupId)
    });
  };

  const conditions = group.conditions || [];
  const innerGroups = group.groups || [];
  const totalItems = conditions.length + innerGroups.length;

  return (
    <div className={cn(
      "border rounded-lg p-3",
      depth > 0 ? "bg-muted/20 ml-4" : "bg-card"
    )}>
      {/* Group Header */}
      <div className="flex items-center gap-2 mb-3">
        {depth > 0 && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-muted-foreground hover:text-foreground"
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        )}

        {/* Match Type Toggle */}
        <div className="inline-flex rounded-md border overflow-hidden">
          <button
            className={cn(
              "px-3 py-1 text-xs font-medium transition-colors",
              group.matchType === 'all'
                ? "bg-primary text-primary-foreground"
                : "bg-background hover:bg-muted"
            )}
            onClick={() => onUpdate(group.id, { matchType: 'all' })}
          >
            ALL
          </button>
          <button
            className={cn(
              "px-3 py-1 text-xs font-medium transition-colors",
              group.matchType === 'any'
                ? "bg-primary text-primary-foreground"
                : "bg-background hover:bg-muted"
            )}
            onClick={() => onUpdate(group.id, { matchType: 'any' })}
          >
            ANY
          </button>
        </div>

        <span className="text-sm text-muted-foreground">
          of the following are true
        </span>

        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={addCondition}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Condition
          </Button>
          {depth < 2 && ( // Limit nesting depth
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={addInnerGroup}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add inner group
            </Button>
          )}
          {depth > 0 && !isOnly && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onDelete(group.id)}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Conditions and Groups */}
      {!isCollapsed && (
        <div className="space-y-2">
          {/* Conditions */}
          {conditions.map((condition) => (
            <ConditionRow
              key={condition.id}
              condition={condition}
              availableFields={availableFields}
              fieldMap={fieldMap}
              onUpdate={updateCondition}
              onDelete={deleteCondition}
              isOnly={totalItems === 1}
            />
          ))}

          {/* Inner Groups */}
          {innerGroups.map((innerGroup) => (
            <ConditionGroup
              key={innerGroup.id}
              group={innerGroup}
              availableFields={availableFields}
              fieldMap={fieldMap}
              onUpdate={updateInnerGroup}
              onDelete={deleteInnerGroup}
              depth={depth + 1}
              isOnly={totalItems === 1}
            />
          ))}

          {/* Empty state */}
          {conditions.length === 0 && innerGroups.length === 0 && (
            <div className="text-center py-4 text-sm text-muted-foreground">
              No conditions yet. Click "Add Condition" to get started.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Conditional Logic Builder Modal
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onOpenChange - Callback when modal open state changes
 * @param {Object} props.currentField - The field being configured
 * @param {Array} props.tasks - All tasks in the workflow
 * @param {string} props.currentTaskId - ID of the task containing the current field
 * @param {Object} props.conditionalLogic - Current conditional logic configuration
 * @param {Function} props.onSave - Callback when saving (receives conditionalLogic object)
 */
export function ConditionalLogicBuilder({
  open,
  onOpenChange,
  currentField,
  tasks,
  currentTaskId,
  conditionalLogic,
  onSave,
  isTaskLevel = false // When true, this is for task/page level conditions (can reference all fields from previous tasks)
}) {
  // Initialize state from props or defaults
  const [logic, setLogic] = useState(() => ({
    mode: conditionalLogic?.mode || 'show_when',
    matchType: conditionalLogic?.matchType || 'all',
    conditions: conditionalLogic?.conditions || [{
      id: generateId(),
      fieldId: '',
      operator: 'equals',
      value: ''
    }],
    groups: conditionalLogic?.groups || []
  }));

  // Reset state when modal opens with new data
  React.useEffect(() => {
    if (open) {
      setLogic({
        mode: conditionalLogic?.mode || 'show_when',
        matchType: conditionalLogic?.matchType || 'all',
        conditions: conditionalLogic?.conditions?.length > 0
          ? conditionalLogic.conditions
          : [{
              id: generateId(),
              fieldId: '',
              operator: 'equals',
              value: ''
            }],
        groups: conditionalLogic?.groups || []
      });
    }
  }, [open, conditionalLogic]);

  // Build available fields from previous tasks
  const { availableFields, fieldMap } = useMemo(() => {
    const fields = [];
    const map = {};

    // Find current task index
    const currentTaskIndex = tasks.findIndex(t => t.id === currentTaskId);
    if (currentTaskIndex === -1) return { availableFields: [], fieldMap: {} };

    // Get fields from all tasks before current task
    for (let i = 0; i < currentTaskIndex; i++) {
      const task = tasks[i];
      const taskFields = (task.content || [])
        .filter(item => item.category === 'form') // Only form fields
        .map((item) => ({
          id: item.id,
          name: item.content?.fieldName || item.label,
          type: item.label, // e.g., 'Dropdown', 'Short Text', etc.
          options: item.content?.options || []
        }));

      if (taskFields.length > 0) {
        fields.push({
          taskId: task.id,
          taskName: task.name || task.formTitle || `Task ${i + 1}`,
          taskIndex: i,
          fields: taskFields
        });

        // Build lookup map
        taskFields.forEach(f => {
          map[f.id] = f;
        });
      }
    }

    // For field-level conditions: also get fields from current task that come before the current field
    // For task-level conditions: skip this (task can only reference fields from previous tasks)
    if (!isTaskLevel) {
      const currentTask = tasks[currentTaskIndex];
      const currentFieldIndex = (currentTask.content || []).findIndex(
        item => item.id === currentField?.id
      );

      if (currentFieldIndex > 0) {
        const currentTaskFields = (currentTask.content || [])
          .slice(0, currentFieldIndex)
          .filter(item => item.category === 'form')
          .map((item) => ({
            id: item.id,
            name: item.content?.fieldName || item.label,
            type: item.label,
            options: item.content?.options || []
          }));

        if (currentTaskFields.length > 0) {
          fields.push({
            taskId: currentTask.id,
            taskName: currentTask.name || currentTask.formTitle || `Task ${currentTaskIndex + 1}`,
            taskIndex: currentTaskIndex,
            fields: currentTaskFields
          });

          currentTaskFields.forEach(f => {
            map[f.id] = f;
          });
        }
      }
    }

    return { availableFields: fields, fieldMap: map };
  }, [tasks, currentTaskId, currentField, isTaskLevel]);

  // Check if there are any available fields
  const hasAvailableFields = availableFields.some(g => g.fields.length > 0);

  // Update root level logic
  const updateLogic = useCallback((updates) => {
    setLogic(prev => ({ ...prev, ...updates }));
  }, []);

  // Update a condition at root level
  const updateCondition = useCallback((conditionId, updates) => {
    setLogic(prev => ({
      ...prev,
      conditions: prev.conditions.map(c =>
        c.id === conditionId ? { ...c, ...updates } : c
      )
    }));
  }, []);

  // Delete a condition at root level
  const deleteCondition = useCallback((conditionId) => {
    setLogic(prev => ({
      ...prev,
      conditions: prev.conditions.filter(c => c.id !== conditionId)
    }));
  }, []);

  // Add condition at root level
  const addCondition = useCallback(() => {
    const newCondition = {
      id: generateId(),
      fieldId: '',
      operator: 'equals',
      value: ''
    };
    setLogic(prev => ({
      ...prev,
      conditions: [...prev.conditions, newCondition]
    }));
  }, []);

  // Add inner group at root level
  const addInnerGroup = useCallback(() => {
    const newGroup = {
      id: generateId(),
      matchType: 'all',
      conditions: [{
        id: generateId(),
        fieldId: '',
        operator: 'equals',
        value: ''
      }],
      groups: []
    };
    setLogic(prev => ({
      ...prev,
      groups: [...prev.groups, newGroup]
    }));
  }, []);

  // Update an inner group
  const updateInnerGroup = useCallback((groupId, updates) => {
    setLogic(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? { ...g, ...updates } : g
      )
    }));
  }, []);

  // Delete an inner group
  const deleteInnerGroup = useCallback((groupId) => {
    setLogic(prev => ({
      ...prev,
      groups: prev.groups.filter(g => g.id !== groupId)
    }));
  }, []);

  // Validate and save
  const handleSave = () => {
    // Filter out incomplete conditions
    const validConditions = logic.conditions.filter(c => c.fieldId && c.operator);
    const validGroups = logic.groups.filter(g =>
      (g.conditions?.length > 0 && g.conditions.some(c => c.fieldId && c.operator)) ||
      (g.groups?.length > 0)
    );

    if (validConditions.length === 0 && validGroups.length === 0) {
      // No valid conditions, save as disabled
      onSave({ mode: 'disabled', conditions: [], groups: [], matchType: 'all' });
    } else {
      onSave({
        ...logic,
        conditions: validConditions,
        groups: validGroups
      });
    }
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const totalItems = logic.conditions.length + logic.groups.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isTaskLevel ? 'Task Conditional Logic' : 'Conditional Logic Builder'}
          </DialogTitle>
          <DialogDescription>
            {isTaskLevel
              ? 'Configure when this task/page should be shown or skipped based on answers from previous tasks.'
              : 'Configure when this field should be shown or hidden based on other field values.'
            }
          </DialogDescription>
        </DialogHeader>

        {!hasAvailableFields ? (
          <div className="flex items-center gap-3 p-4 border rounded-lg bg-amber-50 border-amber-200">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">No fields available for conditions</p>
              <p className="text-amber-700 mt-1">
                {isTaskLevel
                  ? 'Add form fields to previous tasks in your workflow to create conditional logic. This is the first task, so there are no previous fields to reference.'
                  : 'Add form fields to previous tasks in your workflow to create conditional logic.'
                }
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Root Level Group Header */}
            <div className="flex items-center gap-2">
              {/* Match Type Toggle */}
              <div className="inline-flex rounded-md border overflow-hidden">
                <button
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium transition-colors",
                    logic.matchType === 'all'
                      ? "bg-primary text-primary-foreground"
                      : "bg-background hover:bg-muted"
                  )}
                  onClick={() => updateLogic({ matchType: 'all' })}
                >
                  ALL
                </button>
                <button
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium transition-colors",
                    logic.matchType === 'any'
                      ? "bg-primary text-primary-foreground"
                      : "bg-background hover:bg-muted"
                  )}
                  onClick={() => updateLogic({ matchType: 'any' })}
                >
                  ANY
                </button>
              </div>

              <span className="text-sm text-muted-foreground">
                of the following are true
              </span>

              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-sm"
                  onClick={addCondition}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Condition
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-sm"
                  onClick={addInnerGroup}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add inner group
                </Button>
              </div>
            </div>

            {/* Conditions */}
            <div className="space-y-2">
              {logic.conditions.map((condition) => (
                <ConditionRow
                  key={condition.id}
                  condition={condition}
                  availableFields={availableFields}
                  fieldMap={fieldMap}
                  onUpdate={updateCondition}
                  onDelete={deleteCondition}
                  isOnly={totalItems === 1}
                />
              ))}

              {/* Inner Groups */}
              {logic.groups.map((group) => (
                <ConditionGroup
                  key={group.id}
                  group={group}
                  availableFields={availableFields}
                  fieldMap={fieldMap}
                  onUpdate={updateInnerGroup}
                  onDelete={deleteInnerGroup}
                  depth={1}
                  isOnly={totalItems === 1}
                />
              ))}

              {/* Empty state */}
              {logic.conditions.length === 0 && logic.groups.length === 0 && (
                <div className="text-center py-8 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
                  No conditions yet. Click "Add Condition" to get started.
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!hasAvailableFields}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConditionalLogicBuilder;
